// Metabolic Support Follow-up Questionnaire (Sermorelin / Tesamorelin / MIC)
//
// Source of truth: Beluga Health "Metabolic Support Follow-up Questionnaire"
// Google Drive doc 1XMp4qrkuQuGRIGdjRldUUFHglL-jricrIJbXY6tSPK4. Question
// order, conditional branching, consent body, and named-field mapping all
// come from that doc — do not reorder or reword without re-reading the
// source. PURPLE-marked questions (sex, medicalConditions, selfReportedMeds,
// allergies) are extracted by backend `mapAnswersToFormObj` to top-level
// named fields and dropped from the Q/A array.
//
// Entry point: only via the dashboard embed wrapper (?embed=1). The §0.A
// route guard at FollowUpForm.jsx redirects direct traffic, so this file
// is unreachable for self-serve patients.

const heightWeightQuestion = {
  id: 'height-weight',
  type: 'height-weight-bmi',
  question: 'What is your height and weight?',
  subtitle: 'This information helps your provider review your treatment.',
  // Sermorelin/Tesamorelin do not use BMI cutoffs the way GLP-1s do.
  skipBmiCheck: true,
};

const sexAtBirthQuestion = {
  id: 'sex-at-birth',
  type: 'single-select-prefilled',
  question: 'What was your sex assigned at birth?',
  fieldMapping: 'sex',
  options: [
    { label: 'Male', value: 'Male' },
    { label: 'Female', value: 'Female' },
  ],
};

// BLUE — only when sex-at-birth === 'Female'
const pregnancyStatusQuestion = {
  id: 'pregnancy-status',
  type: 'single-select',
  question: 'Are you currently pregnant, breastfeeding or planning to become pregnant?',
  showIf: { field: 'sex-at-birth', value: 'Female' },
  options: [
    { label: 'Yes', value: 'yes', disqualifies: true },
    { label: 'No', value: 'no' },
  ],
};

// BLUE — only when pregnancy-status === 'no'. Body matches Beluga source verbatim.
const pregnancyConsentQuestion = {
  id: 'pregnancy-consent',
  type: 'consent-dropdown',
  question: 'Consent (Pregnancy Risk)',
  showIf: { field: 'pregnancy-status', value: 'no' },
  consentText: `I understand that the medication prescribed to me by my healthcare provider may not be safe to take during pregnancy. I acknowledge that taking this medication while pregnant could pose risks to my health and the health of a developing fetus.

I agree to take necessary precautions to avoid becoming pregnant while using this medication, including the use of effective contraception methods as discussed with my healthcare provider.

I understand that I should stop taking this medication before attempting to become pregnant. I agree to consult with my healthcare provider prior to discontinuing the medication and before planning a pregnancy to ensure my safety and well-being.

By selecting below, I confirm that I have read and understand the information provided above. I consent to proceed with the treatment under these conditions.`,
  options: [
    { label: 'I have read and understand the above information, I understand the risks and wish to proceed', value: 'consent' },
    { label: 'I have read the information and do NOT wish to proceed', value: 'decline', disqualifies: true },
  ],
};

const medicalConditionsQuestion = {
  id: 'medical-conditions',
  type: 'textarea-prefilled',
  question: 'Please identify all your current medical conditions',
  fieldMapping: 'medicalConditions',
  placeholder: 'e.g., Type 2 Diabetes, Hypertension, etc.',
  required: true,
};

const selfReportedMedsQuestion = {
  id: 'self-reported-meds',
  type: 'textarea-prefilled',
  question: 'Please list all your current medications including dosages',
  fieldMapping: 'selfReportedMeds',
  placeholder: 'e.g., Metformin 500mg twice daily, Lisinopril 10mg daily...',
  required: true,
};

const allergiesQuestion = {
  id: 'allergies',
  type: 'textarea-prefilled',
  question: 'Please list all of your known allergies',
  fieldMapping: 'allergies',
  placeholder: 'e.g., Penicillin (causes rash), Shellfish (anaphylaxis)...',
  required: true,
};

const healthChangesQuestion = {
  id: 'health-changes',
  type: 'single-select',
  question: 'Have there been any health changes, medication changes, ED visits or hospitalizations since your last check-in?',
  options: [
    { label: 'No', value: 'no' },
    { label: 'Yes', value: 'yes' },
  ],
};

const healthChangesDetailsQuestion = {
  id: 'health-changes-details',
  type: 'textarea',
  question: 'Please tell us more about the changes in your health',
  showIf: { field: 'health-changes', value: 'yes' },
  placeholder: 'Describe your health changes...',
  required: true,
};

const currentMedicationStatusQuestion = {
  id: 'current-medication-status',
  type: 'single-select',
  question: 'How are you doing on the current medication?',
  options: [
    { label: 'Well', value: 'well' },
    { label: 'Not well', value: 'not-well' },
  ],
};

const notWellDetailsQuestion = {
  id: 'not-well-details',
  type: 'textarea',
  question: 'Please tell us more about what you are experiencing',
  showIf: { field: 'current-medication-status', value: 'not-well' },
  placeholder: 'Describe what you are experiencing...',
  required: true,
};

const activelyTakingQuestion = {
  id: 'actively-taking',
  type: 'single-select',
  question: 'Are you still actively taking this medication?',
  options: [
    { label: 'Yes', value: 'yes' },
    { label: 'No', value: 'no' },
  ],
};

const lastTakenQuestion = {
  id: 'last-taken',
  type: 'textarea',
  question: 'When did you last take this medication?',
  showIf: { field: 'actively-taking', value: 'no' },
  placeholder: 'e.g., About 2 weeks ago, or January 15',
  required: true,
};

const sideEffectsQuestion = {
  id: 'side-effects',
  type: 'single-select',
  question: 'Have you had any side effects from the current medication?',
  options: [
    { label: 'No', value: 'no' },
    { label: 'Yes', value: 'yes' },
  ],
};

const sideEffectsDetailsQuestion = {
  id: 'side-effects-details',
  type: 'textarea',
  question: 'Please tell us more about the side effects you are experiencing',
  showIf: { field: 'side-effects', value: 'yes' },
  placeholder: 'Describe your side effects...',
  required: true,
};

// Multi-select to match intake pattern, but only Hypothyroidism triggers
// the conditional thyroid follow-up. Cancer is listed (not disqualifying)
// per source-of-truth — clinical review happens server-side.
const medicalHistoryQuestion = {
  id: 'medical-history',
  type: 'multi-select',
  question: 'Do you currently have, or have you ever been diagnosed with, any of the following conditions?',
  subtitle: 'Select all that apply',
  options: [
    { label: 'None of these apply to me', value: 'none' },
    { label: 'Any Active Cancer', value: 'active-cancer' },
    { label: 'Hypothyroidism/Underactive Thyroid', value: 'hypothyroidism' },
  ],
};

// BLUE — only shown when medical-history includes 'hypothyroidism'.
// Multi-select answers come back as arrays, so showIf gets a custom
// includes check via the renderer's array-aware comparator (see
// buildDynamicMetabolicFollowUpFlow below).
const thyroidControlledQuestion = {
  id: 'thyroid-controlled',
  type: 'textarea',
  question: 'Is your thyroid disorder well controlled?',
  showIf: { field: 'medical-history', includes: 'hypothyroidism' },
  placeholder: 'e.g., Yes, on levothyroxine 50mcg daily; last TSH in range.',
  required: true,
};

const currentMedicationsQuestion = {
  id: 'current-medications',
  type: 'multi-select',
  question: 'Do you take any of the following medications?',
  subtitle: 'Select all that apply',
  options: [
    { label: 'COX inhibitors (Aspirin, indomethacin)', value: 'cox-inhibitors' },
    { label: 'Muscarinic antagonist (atropine)', value: 'muscarinic-antagonist' },
    { label: 'Somatostatin-containing medications (clonidine and levodopa)', value: 'somatostatin' },
    { label: 'Propylthiouracil', value: 'propylthiouracil' },
    { label: 'None of the above', value: 'none-of-the-above' },
  ],
};

const additionalQuestionsQuestion = {
  id: 'additional-questions',
  type: 'textarea',
  question: 'What other information or questions do you have for the doctor?',
  placeholder: 'Type your questions or concerns here...',
  required: false,
};

// Body matches Beluga source-of-truth verbatim. The intake-flow's
// `main-consent` body is functionally identical (one trailing-period
// nit aside) — keeping these in sync prevents the c523529-style label
// mismatch that flagged a Beluga rejection.
const mainConsentQuestion = {
  id: 'main-consent',
  type: 'consent-dropdown',
  question: 'Consent for Sermorelin/Tesamorelin Therapy',
  consentText: `Medications such as sermorelin and tesamorelin are medications that may be prescribed to support your metabolism, though it is not FDA-approved for this specific use. Its application for metabolism support is considered off-label, meaning it is prescribed by your healthcare provider despite not being specifically approved by the FDA for this purpose. The purpose of this consent form is to ensure that you understand the following:

  - The reasons for using sermorelin or tesamorelin
  - The potential benefits and risks
  - The monitoring plan
  - Your responsibilities during treatment

Please read this document carefully.

Sermorelin and tesamorelin are being considered as a treatment option to potentially improve your metabolic support by acting as a synthetic analogue of growth hormone-releasing hormone (GHRH), which stimulates the secretion of growth hormone (GH). Growth hormone plays a critical role in:

  - Growth
  - Body composition
  - Cell repair
  - Metabolism

By promoting the natural production of GH, these medications may provide the following benefits:

  - Enhance muscle growth
  - Improve body composition
  - Boost energy levels
  - Support overall physical performance

It is commonly used for purposes like weight loss, muscle gain, and anti-aging therapy.

As previously mentioned, the use of sermorelin and tesamorelin to increase growth hormone levels is considered off-label. While legally prescribed, its is not an FDA-approved indication.

Like any medication, these comes with potential risks and side effects:

Common side effects:

  - Injection site reactions (pain, swelling, redness) if provided as an injection
  - Headaches
  - Flushing
  - Dizziness
  - Nausea

Less common but more serious risks:

  - Exacerbation of undiagnosed cancers
  - Significant changes in blood pressure
  - Increased intracranial pressure
  - Severe allergic reactions

There are also concerns regarding the long-term use and its potential link to increased cancer risk. While there is no definitive evidence directly linking these to cancer, some studies suggest that prolonged use of growth hormone therapies could potentially increase the risk of certain cancers due to hormonal imbalances. Since these products stimulate the production of growth hormone, it may influence the development of cancerous cells in susceptible individuals.

To ensure safety and effectiveness, regular monitoring is required during treatment including:

  - Blood tests every six months to monitor growth hormone levels
  - Regular symptom assessments to evaluate the medication's effectiveness
  - Follow-up visits to discuss progress, side effects, and necessary adjustments to the treatment plan

You have the right to discuss and consider alternative treatments with your healthcare provider. These alternatives may include:

  - Lifestyle modifications (exercise, diet, sleep hygiene, stress management)

As a patient, you are responsible for:

  - Attending all scheduled follow-up appointments
  - Reporting any side effects or new symptoms to your healthcare provider
  - Adhering to the prescribed dosing regimen
  - Undergoing all required laboratory tests as part of the monitoring plan if needed

By selecting below, you confirm that you:

  - Have read and understood the information provided
  - Understand the potential risks and benefits of using sermorelin or tesamorelin, as well as its off-label nature
  - Agree to adhere to the monitoring plan and report any concerns or side effects to your healthcare provider`,
  options: [
    { label: 'I have read the consent and understand the content, I wish to proceed', value: 'consent' },
    { label: 'I have read the consent and I do NOT wish to move forward', value: 'decline', disqualifies: true },
  ],
};

const checkoutQuestion = {
  id: 'checkout',
  type: 'checkout',
  question: 'Review treatment and pay',
};

export const metabolicFollowUpQuestions = {
  sexAtBirth: sexAtBirthQuestion,
  pregnancyStatus: pregnancyStatusQuestion,
  pregnancyConsent: pregnancyConsentQuestion,
  heightWeight: heightWeightQuestion,
  medicalConditions: medicalConditionsQuestion,
  selfReportedMeds: selfReportedMedsQuestion,
  allergies: allergiesQuestion,
  healthChanges: healthChangesQuestion,
  healthChangesDetails: healthChangesDetailsQuestion,
  currentMedicationStatus: currentMedicationStatusQuestion,
  notWellDetails: notWellDetailsQuestion,
  activelyTaking: activelyTakingQuestion,
  lastTaken: lastTakenQuestion,
  sideEffects: sideEffectsQuestion,
  sideEffectsDetails: sideEffectsDetailsQuestion,
  medicalHistory: medicalHistoryQuestion,
  thyroidControlled: thyroidControlledQuestion,
  currentMedications: currentMedicationsQuestion,
  additionalQuestions: additionalQuestionsQuestion,
  mainConsent: mainConsentQuestion,
  checkout: checkoutQuestion,
};

// Order matches the Beluga source-of-truth doc top-to-bottom. Note: there
// is intentionally NO medication-selection slide — the patient continues
// with their existing patientPreference (set on their original metabolic
// intake). The CheckoutStep reads that from userData and renders the
// matching plan list.
export const metabolicFollowUpFlow = [
  'sex-at-birth',
  'pregnancy-status',
  'pregnancy-consent',
  'height-weight',
  'medical-conditions',
  'self-reported-meds',
  'allergies',
  'health-changes',
  'health-changes-details',
  'current-medication-status',
  'not-well-details',
  'actively-taking',
  'last-taken',
  'side-effects',
  'side-effects-details',
  'medical-history',
  'thyroid-controlled',
  'current-medications',
  'additional-questions',
  'main-consent',
  'checkout',
];

export const getMetabolicQuestionById = (id) =>
  Object.values(metabolicFollowUpQuestions).find((q) => q.id === id);

// Goal-type detection. Order:
// 1. Explicit URL override `?type=metabolic|antiAging|weightloss` (smoke-test
//    escape hatch — production embed callers should rely on auto-detection)
// 2. userData.primaryGoal === 'muscle-building' → metabolic
// 3. userData.primaryGoal === 'anti-aging' or 'general-wellness' → antiAging
// 4. userData.patientPreference[0].name match — anti-aging-specific products
//    (nad/glutathione/metformin) win first to avoid the MIC ambiguity below
// 5. userData.patientPreference[0].name match sermorelin/tesamorelin/MIC → metabolic
// 6. Default: 'weightloss' (existing GLP-1 followup)
export const detectGoalType = (userData, urlTypeParam) => {
  if (urlTypeParam === 'antiAging') return 'antiAging';
  if (urlTypeParam === 'metabolic') return 'metabolic';
  if (urlTypeParam === 'weightloss') return 'weightloss';
  if (urlTypeParam === 'trt') return 'trt';
  if (urlTypeParam === 'womenSexualHealth') return 'womenSexualHealth';
  if (urlTypeParam === 'mensSexualHealth') return 'mensSexualHealth';
  if (urlTypeParam === 'brandedWeightloss') return 'brandedWeightloss';
  if (userData?.primaryGoal === 'muscle-building') return 'metabolic';
  if (userData?.primaryGoal === 'anti-aging' || userData?.primaryGoal === 'general-wellness') {
    return 'antiAging';
  }
  if (userData?.primaryGoal === 'trt' || userData?.primaryGoal === 'testosterone') {
    return 'trt';
  }
  const med = userData?.patientPreference?.[0]?.name?.toLowerCase() || '';
  if (med.includes('nad') || med.includes('glutathione') || med.includes('metformin')) {
    return 'antiAging';
  }
  if (med.includes('sermorelin') || med.includes('tesamorelin') || med.includes('mic')) {
    return 'metabolic';
  }
  // TRT-specific prefs — testosterone (any formulation), enclomiphene (SERM),
  // anastrozole (AI), HCG. Pass the explicit `?type=trt` from the dashboard
  // for safety; auto-detect is a backstop.
  if (
    med.includes('testosterone') ||
    med.includes('enclomiphene') ||
    med.includes('clomiphene') ||
    med.includes('anastrozole') ||
    med.includes('hcg')
  ) {
    return 'trt';
  }
  // Men's sexual health — apomorphine + PDE5i (sildenafil/tadalafil) are
  // men-only signals (PT-141/oxytocin overlap with the women's flow, so those
  // alone can't disambiguate — the dashboard passes the explicit
  // ?type=mensSexualHealth, which takes priority above). Checked before the
  // women's PT-141/oxytocin backstop so a men's parent isn't mis-routed.
  if (
    med.includes('apomorphine') ||
    med.includes('sildenafil') ||
    med.includes('tadalafil') ||
    med.includes('vardenafil') ||
    med.includes('avanafil')
  ) {
    return 'mensSexualHealth';
  }
  // Women's sexual health — PT-141 (Bremelanotide), oxytocin combo. Pass the
  // explicit ?type=womenSexualHealth from the dashboard; this is a backstop.
  if (
    med.includes('pt-141') ||
    med.includes('pt141') ||
    med.includes('bremelanotide') ||
    med.includes('oxytocin')
  ) {
    return 'womenSexualHealth';
  }
  // Branded GLP-1 weightloss — brand names are a distinct signal from the
  // compounded flow (which uses generic semaglutide/tirzepatide naming). The
  // dashboard should pass the explicit ?type=brandedWeightloss; auto-detect is
  // a backstop.
  if (
    med.includes('wegovy') ||
    med.includes('ozempic') ||
    med.includes('zepbound') ||
    med.includes('mounjaro') ||
    med.includes('foundayo')
  ) {
    return 'brandedWeightloss';
  }
  return 'weightloss';
};

// Builds the visible flow given current answers + userData. Mirrors
// `buildDynamicFollowUpFlow` in followUpData.js but for the metabolic
// question set. Multi-select `medical-history` triggers `thyroid-controlled`
// via the `includes:` shorthand in the question's showIf.
export const buildDynamicMetabolicFollowUpFlow = (answers, userData) => {
  const flow = [];

  for (const questionId of metabolicFollowUpFlow) {
    const question = getMetabolicQuestionById(questionId);
    if (!question) continue;

    if (question.showIf) {
      const { field, value, includes } = question.showIf;
      const current = answers[field];
      if (includes !== undefined) {
        if (!Array.isArray(current) || !current.includes(includes)) continue;
      } else if (current !== value) {
        continue;
      }
    }

    if (questionId === 'pregnancy-status' || questionId === 'pregnancy-consent') {
      const sex = answers['sex-at-birth'] || userData?.sex;
      if (sex === 'Male') continue;
    }

    flow.push(question);
  }

  return flow;
};

export default metabolicFollowUpQuestions;
